import React, { useEffect, useMemo, useState } from 'react';
import { Popover } from '@mantine/core';
import { Icon } from '@shakkerai/components';
import { useUserInfo, setTempState, useTempState } from '@starflow/app/store';
import { useUserVipInfo } from '@shakkerai/pro-commercial';
import { clearUsertokenToCookie, setUsertokenToCookie } from '@shakkerai/util-cookie';
import { setUserinfoToLocalStorage } from '@shakkerai/util-localstorage/src/auth';
import { useGenHistoryVisible } from '@shakkerai/pro-gen-history/src/store/base';
import { translate } from '@shakkerai/util-i18n';
import { useClickOutside } from '@mantine/hooks';
import { config } from '@shakkerai/config';
import { isMainland, isOverseas } from '@shakkerai/pro-utils';
import { logout } from '@shakkerai/services';
import { useGuidanceHooks, useGuidanceRestartHooks } from '@shakkerai/pro-guidance';
import logoNew from './assets/logo_new.png';
import picLogo from './assets/logo.png';
import { RightMenuItem, RightMenu, Dropdown, GuidePopover, Text } from '@shakkerai/pro-components';
import iconDiscord from './assets/icon_discord.svg';
import { mc } from '@shakkerai/report';
import { ElementTools } from './ElementTools';
import { CanvasTools } from './CanvasTools';
import powerIcon from '@shakkerai/pro-components/src/button/power.svg';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import styles from './navbar.module.scss';
import './navbar.scss';
export * from './constants';

const Profile = React.memo(({ userInfo, onLogout }) => {
  const { guidanceEntryVisible, closeGuidanceEntry } = useGuidanceRestartHooks();
  const [opened, setOpened] = useState(false);
  const ref = useClickOutside(() => setOpened(false));

  useEffect(() => {
    if (guidanceEntryVisible) {
      setOpened(true);
      setTimeout(() => {
        closeGuidanceEntry();
      }, 4500);
    } else {
      setOpened(false);
    }
    // eslint-disable-next-line
  }, [guidanceEntryVisible]);

  return (
    <Popover
      position="bottom-end"
      opened={opened}
      withArrow
      offset={{ mainAxis: 18, crossAxis: 0 }}
      arrowSize={10}
      arrowRadius={1}
    >
      <Popover.Target>
        <div
          ref={ref}
          onClick={() => setOpened(true)}
          className="flex items-center justify-center mx-3 w-7 h-7 bg-white shadow-light rounded-full cursor-pointer"
        >
          <img src={convert2CompressedUrl(userInfo.avatar, 56)} className="w-7 h-7 rounded-full object-cover" />
        </div>
      </Popover.Target>
      <Dropdown className="!border-none !bg-[#2c2c2c] !p-0">
        <RightMenu
          visible={true}
          style={{
            width: 120,
            zIndex: 1,
          }}
        >
          <RightMenuItem onClick={onLogout}>{translate('user_logout')}</RightMenuItem>
        </RightMenu>
      </Dropdown>
    </Popover>
  );
});

export const NavButton = React.memo(
  React.forwardRef((props, ref) => {
    const { iconName, name, onClick, disabled, opened, children } = props;

    return (
      <button
        name={name}
        disabled={opened || disabled}
        onClick={onClick}
        ref={ref}
        className={`text-[#D3D3D3] h-full flex items-center ${
          disabled ? 'opacity-30 cursor-not-allowed' : 'disabled:text-primary '
        } ${name === 'image' ? 'translate-y-px' : ''} ${styles.navButton} `}
      >
        {children ? (
          children
        ) : (
          <Icon className={`${iconName} ${opened ? 'font-extrabold' : 'font-normal'}`} fontSize={16} color="inherit" />
        )}
      </button>
    );
  }),
);

export const Navbar = React.memo(() => {
  const { userInfo, updateUserInfo } = useUserInfo();
  const { toggleHistoryModelsBar } = useGenHistoryVisible();
  const [isShowSharePicPopover, setIsShowSharePicPopover] = useState(false);
  const userLogin = !!userInfo?.id;

  const goHome = () => {
    if (config && config.env === 'aws') {
      window.location.href = '/';
    } else {
      window.location.href = '/';
    }
  };

  const handleLogOut = async () => {
    if (isMainland) {
      await logout();
    }

    clearUsertokenToCookie();
    updateUserInfo({ userInfo: null });
    setUsertokenToCookie(null);
    setUserinfoToLocalStorage(null);

    localStorage.removeItem('shakker-viewport');
    localStorage.removeItem('liblib_stage_initailized');
    if (window.location.pathname === '/pro') {
      window.location.reload();
    } else {
      goHome();
    }
  };
  const firstGeneratedDoneFlag = useTempState('firstGeneratedDoneFlag');

  // 判断右上角hostory是否需要展示【图片分享】的提示弹窗
  const isShowSharePicTips = useTempState('isShowSharePicTips');
  useEffect(() => {
    if (isShowSharePicTips) {
      setIsShowSharePicPopover(true);
    }
  }, [isShowSharePicTips]);

  // const { visible } = useGuidanceHooks(['Enter', 'ChooseStyle', 'Generate', 'Remix', 'RemixNow', 'GeneratePrompt']);

  const { visible: guidance9Visible } = useGuidanceHooks(['Import']);

  // const { visible: guidanceRemixNowVisible } = useGuidanceHooks(['RemixNow', 'GeneratePrompt']);

  const { remainedPower, loadVipInfo } = useUserVipInfo();

  useEffect(() => {
    loadVipInfo();
    // eslint-disable-next-line
  }, []);

  const vipNode = useMemo(() => {
    return (
      <div
        className="flex items-center ml-[16px] cursor-pointer"
        onClick={() => {
          if (isMainland) {
            // window.open('https://www-test.liblib.art/viphome');
            window.open('https://www.liblib.art/viphome'); // 生产更换
          } else {
            window.open('/purchase?source=pro');
          }
        }}
      >
        <img src={powerIcon} className="w-2.5 mr-1" alt="" />
        <div className={styles.vipRemainPoint}>{remainedPower}</div>
      </div>
    );
  }, [remainedPower]);

  return (
    <div
      className={`absolute pro-nav-wrapper top-0 left-0 flex items-center w-screen min-w-[1280px] h-12 px-4 bg-[#2C2C2C] justify-between ${
        guidance9Visible ? 'z-[71]' : 'z-[51]'
      }`}
    >
      <div className="flex items-center w-[400px] h-full" data-left>
        <div onClick={goHome} className="flex items-center justify-center h-8 mr-2 cursor-pointer shadow-light">
          {isMainland ? (
            <img draggable={false} src={picLogo} className="h-[18px]" />
          ) : (
            <img draggable={false} className="w-8 h-8" src={logoNew} />
          )}
        </div>
        <CanvasTools />
        <div className="flex items-center">
          {
            isOverseas ? (
              <button
                onClick={() => {
                  window.open('https://discord.gg/djyP5u3vHY');
                  mc('tutorial');
                }}
                className="flex items-center ml-4"
              >
                <img src={iconDiscord} className="w-4" alt="" />
                <span className="ml-1 text-[#636ae1] translate-y-px text-xs font-bold">Help</span>
              </button>
            ) : null
            // <Popover>
            //   <Popover.Target>
            //     <button className="flex items-center ml-4">
            //       <img src={iconQQ} className="w-4" alt="" />
            //       <span className="ml-1 text-[#636ae1] translate-y-px text-xs font-bold">帮助</span>
            //     </button>
            //   </Popover.Target>
            //   <Popover.Dropdown className="p-0 rounded-[2px] overflow-hidden border-none shadow-lg">
            //     <div className="flex flex-col items-center justify-center w-[200px] py-4 bg-white border-t-2 border-primary">
            //       <span className="text-sm font-medium text-[#383838]">QQ群</span>
            //       <div className="flex items-center mt-1">
            //         <span className="text-xs text-[#383838]">群号：{qqGroupNumber}</span>
            //         <div onClick={handleCopyQQGroupNumber} className="ml-1 cursor-pointer flex-shrink-0 text-primary">
            //           <Icon className="icon-copy" fontSize={12} color="inherit" />
            //         </div>
            //       </div>
            //       <img src={qqGroup} className="w-[100px] h-[100px] mt-1 cursor-pointer" alt="" />
            //       <span className="mt-3 text-xs text-[#7a7a7a]">QQ 扫码加群</span>
            //     </div>
            //   </Popover.Dropdown>
            // </Popover>
          }
        </div>
        <div
          className={styles.tutorials}
          onClick={() => {
            if (isOverseas) {
              window.open(
                'https://docs.google.com/document/d/1jJR-HNm8vS36d4gd7H7q2U8x9FuX3bKC63hiDq5Lk0U/edit?tab=t.0#heading=h.o59cmgdxkbtq',
              );
            } else {
              window.open('https://liblibai.feishu.cn/wiki/VQN7w4SSOi1dvpkkqLmcd6O4nZM');
            }
          }}
        >
          <Icon className="shk-dingbar-Tutorials" color="inherit"></Icon>
          <span className="font-semibold">{translate('tutorials')}</span>
        </div>
        <div
          id="helpButtonEntry"
          className={styles.tutorials}
          onClick={() => setTempState({ newerGuideTipOpen: true })}
        >
          <Icon className="icon-problem" fontSize={20} color="inherit" />
          <GuidePopover
            visible={firstGeneratedDoneFlag}
            title={translate('guideTip_finish_title')}
            position="bottom"
            width={280}
            showFooter={false}
            offset={{ mainAxis: 20, crossAxis: 40 }}
            content={
              <span className="flex items-center">
                {translate('guideTip_finish_desc_1')}
                <Icon className="icon-problem" fontSize={20} color="inherit" />
                {translate('guideTip_finish_desc_2')}
              </span>
            }
            onClose={() => {
              setTempState({ firstGeneratedDoneFlag: false });
            }}
          />
        </div>
      </div>
      <div className="top-image-tools-container">
        <ElementTools />
      </div>
      <div className="flex items-center justify-end h-full" data-right>
        {vipNode}
        {userLogin && <Profile userInfo={userInfo} onLogout={handleLogOut} />}
        {userLogin && (
          <Popover
            opened={isShowSharePicPopover}
            arrowPosition="side"
            arrowOffset={27}
            onChange={setIsShowSharePicPopover}
            clickOutsideEvents={['mouseup', 'touchend']}
            withArrow
            position="bottom-end"
            offset={19}
            width={323}
          >
            <Popover.Target>
              <button
                onClick={() => (toggleHistoryModelsBar(), setTempState({ showNormalCkptGide: false }))}
                data-role="gen-history-trigger"
                className={`flex items-center ml-3`}
              >
                <Icon className="icon-border_reviewing" fontSize={14} color="white" />
                <span className="ml-1 text-[white] text-xs font-semibold">{translate('history_entrance')}</span>
              </button>
            </Popover.Target>
            <Popover.Dropdown className={styles.guidancePopover}>
              <div className={styles.wrapper}>
                {<h5 className={styles.title}>{translate('public_tips_title')}</h5>}
                <Text className={styles.content}>{translate('public_tips_desc')}</Text>
                <button onClick={() => setIsShowSharePicPopover(false)} className={styles.btn}>
                  {translate('guidance_got_button')}
                </button>
              </div>
            </Popover.Dropdown>
          </Popover>
        )}
      </div>
    </div>
  );
});
